/**
 * The working draft behind the Expense Claim Request screen.
 *
 * A draft row is deliberately not a `ClaimRow`: `amount` stays a string while
 * the requester is typing (so a half-entered "12." is not collapsed to 12), and
 * it carries two browser-only fields — a local object URL for previewing the
 * receipt the row came from, and the original file name — neither of which the
 * server stores as part of the row's money data.
 */
export interface DraftRow {
  /** Local identity only. The server assigns row ids on submit. */
  id: string;
  date: string;
  receipt: string;
  company: string;
  tin: string;
  description: string;
  amount: string;
  /**
   * What the receipt was printed in, and what it said, kept beside the peso
   * figure so an approver can see where the amount came from. 'PHP' with a null
   * rate is the ordinary case — nothing was converted.
   */
  currency: string;
  originalAmount: string;
  /** The rate `amount` was reached at, as it stood when the row was read. */
  fxRate: number | null;
  vatStatus: 'Yes' | 'No';
  /**
   * The VAT breakdown as printed on the receipt, kept beside the total so it
   * can be checked against whatever the requester ends up typing. Null on a
   * non-VAT receipt, on a row typed in by hand, and on anything read before
   * this was added.
   */
  printedNet: number | null;
  printedVat: number | null;
  /** S3 object key for the uploaded receipt, never the bytes themselves. */
  imageKey: string | null;
  /** Object URL for the file this row was extracted from; preview only. */
  previewUrl: string | null;
  sourceName: string;
  /**
   * A problem only the server could see — a receipt number already used on
   * someone else's claim, or a file it could not read. Everything else
   * (missing date, wrong period, duplicate within this claim) is re-derived
   * live from the row itself, so it clears the moment the row is corrected.
   * This one is cleared explicitly when the receipt number changes.
   */
  serverAnomaly: { kind: string; message: string } | null;
}

export interface DraftState {
  period: string;
  /** Set only when resubmitting a disapproved claim. */
  editingClaimId: string | null;
  rows: DraftRow[];
}

let rowSeq = 0;

export function newRowId(): string {
  rowSeq += 1;
  return `r${rowSeq}`;
}

export function makeRow(init: Partial<DraftRow> = {}): DraftRow {
  return {
    id: newRowId(),
    date: init.date ?? '',
    receipt: init.receipt ?? '',
    company: init.company ?? '',
    tin: init.tin ?? '',
    description: init.description ?? '',
    amount: init.amount ?? '',
    currency: (init.currency ?? 'PHP').toUpperCase(),
    originalAmount: init.originalAmount ?? '',
    fxRate: init.fxRate ?? null,
    vatStatus: init.vatStatus === 'Yes' ? 'Yes' : 'No',
    printedNet: init.printedNet ?? null,
    printedVat: init.printedVat ?? null,
    imageKey: init.imageKey ?? null,
    previewUrl: init.previewUrl ?? null,
    sourceName: init.sourceName ?? '',
    serverAnomaly: init.serverAnomaly ?? null,
  };
}

/** The current month, which is what a new claim covers by default. */
export function currentPeriod(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function emptyDraft(period: string = currentPeriod()): DraftState {
  return { period, editingClaimId: null, rows: [] };
}

export type DraftAction =
  | { type: 'reset'; state: DraftState }
  | { type: 'setPeriod'; period: string; rows: DraftRow[] }
  | { type: 'addRows'; rows: DraftRow[] }
  | { type: 'updateRow'; id: string; patch: Partial<DraftRow> }
  | { type: 'deleteRow'; id: string };

export function draftReducer(state: DraftState, action: DraftAction): DraftState {
  switch (action.type) {
    case 'reset':
      return action.state;
    case 'setPeriod':
      return { ...state, period: action.period, rows: action.rows };
    case 'addRows':
      return action.rows.length === 0 ? state : { ...state, rows: [...state.rows, ...action.rows] };
    case 'updateRow':
      return {
        ...state,
        rows: state.rows.map((r) => {
          if (r.id !== action.id) return r;
          const next = { ...r, ...action.patch };
          // A server-found duplicate is about one specific receipt number.
          // Editing that number makes the finding stale, so it is cleared and
          // re-checked rather than left to block submission forever.
          if (
            next.serverAnomaly?.kind === 'duplicate_elsewhere' &&
            action.patch.receipt !== undefined &&
            action.patch.receipt !== r.receipt
          ) {
            next.serverAnomaly = null;
          }
          return next;
        }),
      };
    case 'deleteRow':
      return { ...state, rows: state.rows.filter((r) => r.id !== action.id) };
  }
}

export interface RowTotals {
  vat: number;
  net: number;
  total: number;
}

/** Philippine VAT is 12% inclusive, so it is backed out of the gross amount. */
const VAT_RATE_DIVISOR = 1.12;

export function computeRow(row: DraftRow): RowTotals {
  const total = Number(row.amount) || 0;
  const vat = row.vatStatus === 'Yes' ? total - total / VAT_RATE_DIVISOR : 0;
  return { vat, net: total - vat, total };
}

export function draftTotals(rows: readonly DraftRow[]): RowTotals {
  return rows.reduce<RowTotals>(
    (acc, row) => {
      const t = computeRow(row);
      return { vat: acc.vat + t.vat, net: acc.net + t.net, total: acc.total + t.total };
    },
    { vat: 0, net: 0, total: 0 },
  );
}

/** The row shape `claims.create` / `claims.resubmit` accept. */
export interface WireRow {
  date: string;
  receipt: string;
  company: string;
  tin: string;
  description: string;
  amount: number;
  currency: string;
  originalAmount: number | null;
  vatStatus: 'Yes' | 'No';
  imageKey: string | null;
  sourceName: string;
}

export function toWireRow(row: DraftRow): WireRow {
  return {
    date: row.date,
    receipt: row.receipt.trim(),
    company: row.company.trim(),
    tin: row.tin.trim(),
    description: row.description.trim(),
    amount: Number(row.amount) || 0,
    currency: row.currency || 'PHP',
    // Sent so the server knows what was on the receipt. The rate is NOT sent —
    // the server reapplies Finance's current rate itself, so a tampered browser
    // cannot name the rate its own claim converts at.
    originalAmount: row.originalAmount === '' ? null : Number(row.originalAmount),
    vatStatus: row.vatStatus,
    imageKey: row.imageKey,
    sourceName: row.sourceName,
  };
}
